import React, { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import QuickAccessCard from './components/QuickAccessCard';
import JornadaAnioPeriodoNivelCursoAsig from '../../components/Filters/JornadaAnioPeriodoNivelCursoAsig';
import organizacionAcademicaService from '../../services/organizacionAcademicaService';
import classroomService from '../../services/classroomService';
import utils from '../../utils/generalFunctions';
import types from '../../services/types';
import Loading from '../../components/Loading';
import ClassroomManage from './Classroom/ClassroomManage';
import './AulaVirtual.css';

// Página de inicio del Aula Virtual.
// Para docente/admin: filtro académico (jornada/año/periodo/nivel/curso/
// asignatura, mismo componente que usa "Gestión de Logros") — al buscar,
// carga el aula virtual asociada a esa combinación y entra directo a
// gestionarla.
// Para estudiante/padre: el filtro no aplica (no eligen curso/asignatura,
// ven las suyas), así que se muestra la guía + accesos rápidos de siempre.
const AulaVirtualHome = () => {
  const user = utils.getUserInformation();
  const esDocenteOAdmin = [types.userTypes.TEACHER, types.userTypes.ADMIN].includes(Number(user?.userTypeId));

  return (
    <div className="container-fluid p-0 av-module">
      <div className="av-hero mb-4">
        <h1 className="h3 mb-2">Aula Virtual</h1>
        <p className="mb-0">
          {esDocenteOAdmin
            ? 'Busca el curso y la asignatura para entrar a gestionar su aula virtual.'
            : 'Aquí encuentras tus aulas, tus tareas y todo lo que necesitas para tus clases.'}
        </p>
      </div>

      {esDocenteOAdmin ? <BuscadorAula /> : <AccesosEstudiante />}
    </div>
  );
};

// Recuerda la última aula encontrada en /auladash en sessionStorage (por
// pestaña, se limpia sola al cerrarla) — así, si el docente entra a una
// tarea/examen/foro y vuelve con el botón "Atrás" del navegador, el
// componente se vuelve a montar desde cero (React pierde todo su estado
// local) pero puede reconstruir el aula ya cargada en vez de mostrar el
// filtro vacío otra vez. Se guarda con la clave por userId para no
// arrastrar la selección de un docente a otro si se cambia de sesión en
// la misma pestaña.
const storageKey = () => {
  const user = utils.getUserInformation();
  return `av_auladash_aula_${user?.userId || 'anon'}`;
};

const leerAulaGuardada = () => {
  try {
    const raw = sessionStorage.getItem(storageKey());
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const guardarAulaEnStorage = (aula) => {
  try {
    if (aula) sessionStorage.setItem(storageKey(), JSON.stringify(aula));
    else sessionStorage.removeItem(storageKey());
  } catch {
    // localStorage/sessionStorage deshabilitado (modo privado, cuota
    // llena, etc.) — no es crítico, simplemente no persiste.
  }
};

const BuscadorAula = () => {
  const filteReference = useRef(null);
  const [filters, setFilters] = useState({
    teacherId: '0',
    journeyId: '0',
    schoolYearId: '0',
    periodId: '0',
    educationLevelId: '0',
    courseId: '0',
    schoolSubjectId: '0'
  });
  const [buscando, setBuscando] = useState(false);
  // aula ya restaurada desde una visita anterior a esta misma pestaña:
  // arranca colapsado y con el aula cargada de una, sin que el docente
  // tenga que volver a buscarla.
  const [aula, setAula] = useState(leerAulaGuardada);
  // El filtro se muestra expandido hasta encontrar un aula; al buscar con
  // éxito se colapsa a un resumen (para no perder pantalla) pero el
  // componente del filtro SIGUE montado (solo se oculta con CSS), así
  // conserva la selección hecha y se puede reabrir para cambiarla sin
  // perder nada ni tener que volver a entrar al aula virtual.
  const [expandido, setExpandido] = useState(() => !leerAulaGuardada());

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const buscar = async () => {
    if (filters.courseId === '0' || filters.schoolSubjectId === '0' || filters.schoolYearId === '0') {
      Swal.fire('Completa el filtro', 'Selecciona año escolar, curso y asignatura para buscar el aula.', 'warning');
      return;
    }
    setBuscando(true);
    try {
      const asignaturas = await organizacionAcademicaService.getAcademicOrganizationsByCourse(filters.courseId, filters.schoolYearId);
      const setting = (asignaturas || []).find((a) => String(a.schoolSubjectId) === String(filters.schoolSubjectId));
      if (!setting) {
        Swal.fire('No encontrada', 'No hay una configuración académica para esa combinación.', 'info');
        setBuscando(false);
        return;
      }
      const classroomId = await classroomService.getClassroomByAcademicSetting(setting.academicSettingId).then((r) => r?.classroomId);
      if (!classroomId) {
        Swal.fire('No encontrada', 'Esta asignatura todavía no tiene un aula virtual creada.', 'info');
        setBuscando(false);
        return;
      }
      const resumen = [setting.journeyName, setting.schoolYear, setting.educationLevelName, setting.courseName, setting.schoolSubjectName]
        .filter(Boolean)
        .join(' · ');
      const encontrada = { classroomId, resumen };
      setAula(encontrada);
      guardarAulaEnStorage(encontrada);
      setExpandido(false);
    } catch (error) {
      console.error(error);
      Swal.fire('Ha ocurrido un error', 'Por favor intentelo más tarde', 'error');
    }
    setBuscando(false);
  };

  return (
    <>
      <div className="card mb-3">
        <div className="card-body">
          {!expandido && aula && (
            <button type="button" className="av-filtro-resumen" onClick={() => setExpandido(true)}>
              <i className="fas fa-filter"></i>
              <span>{aula.resumen}</span>
              <span className="av-filtro-resumen-cambiar"><i className="fas fa-pen"></i> Cambiar filtros</span>
            </button>
          )}
          {/* No se desmonta al colapsar: así el filtro conserva la selección hecha */}
          <div className="row mb-0 align-items-end" style={expandido ? undefined : { display: 'none' }}>
            <JornadaAnioPeriodoNivelCursoAsig
              callback={handleFilterChange}
              ref={filteReference}
              properties={{ className: 'col-md-3 mb-3', id: 'aulaVirtualDash', filtro: false }}
            />
            <div className="col-md-2 mb-3 d-flex align-items-end">
              <button className="btn btn-primary w-100" onClick={buscar} disabled={buscando}>
                {buscando ? 'Buscando...' : 'Buscar'} <i className="fas fa-magnifying-glass ms-1"></i>
              </button>
            </div>
          </div>
          {buscando && <Loading />}
        </div>
      </div>

      {aula && <ClassroomManage key={aula.classroomId} classroomId={aula.classroomId} />}
    </>
  );
};

const AccesosEstudiante = () => {
  const accesosRapidos = [
    { icono: 'fa-graduation-cap', color: '#4f8cff', titulo: 'Mis Cursos', descripcion: 'Ve todas tus aulas virtuales y continúa donde ibas.', link: '/cursos' },
  ];
  return (
    <div className="row mb-4">
      {accesosRapidos.map((acceso) => (
        <div className="col-md-6 col-lg-4 mb-3" key={acceso.titulo}>
          <QuickAccessCard {...acceso} />
        </div>
      ))}
    </div>
  );
};

export default AulaVirtualHome;
